import { FormEvent, useState } from 'react';

import { type ApiOrg, isValidOrgName } from '@kaiizen/kai-pro-api';
import { useCreateOrg } from '@kaiizen/kai-pro-api-react-state';

import { useSuperAdminSwitchToOrg } from '@/Client/State';
import { CommonActionButton } from '@kaiizen/kai-shiki/react';

import { SuperAdminOrgLiveCheckbox, SuperAdminOrgNameField } from './SuperAdminOrgFields';
import { getErrorMessage } from './superAdminToolsLib';
import { DEFAULT_SUPER_ADMIN_TOOLS_PANEL_STYLES, SuperAdminToolsPanelStyles } from './superAdminToolsPanelStyles';

/** Super admin form for creating a new org, then switching into it. */
export const SuperAdminCreateOrg = ({ styles = DEFAULT_SUPER_ADMIN_TOOLS_PANEL_STYLES }: { styles?: SuperAdminToolsPanelStyles }) => {
  const [name, setName] = useState('');
  const [isLive, setIsLive] = useState(false);
  const [createdOrg, setCreatedOrg] = useState<ApiOrg>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const createOrg = useCreateOrg();
  const switchToOrg = useSuperAdminSwitchToOrg();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setErrorMessage(undefined);
    try {
      setCreatedOrg(await createOrg.mutate({ name: name.trim(), isLive }));
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  const startOver = () => {
    setCreatedOrg(undefined);
    setName('');
    setIsLive(false);
  };

  if (createdOrg) {
    return (
      <div css={styles.panel}>
        <div css={styles.success}>{`Created ${createdOrg.name}`}</div>
        <div css={styles.description}>{'You are not a member of the new org. Switch to it to set it up and invite its users.'}</div>
        <div css={styles.actions}>
          <CommonActionButton onClick={() => switchToOrg(createdOrg.id)}>{`Switch to ${createdOrg.name}`}</CommonActionButton>
          <CommonActionButton onClick={startOver}>{'Create another'}</CommonActionButton>
        </div>
      </div>
    );
  }

  return (
    <form css={styles.panel} onSubmit={submit}>
      <div css={styles.heading}>{'Create Org'}</div>
      <SuperAdminOrgNameField name={name} onChange={setName} />
      <SuperAdminOrgLiveCheckbox isLive={isLive} onChange={setIsLive} styles={styles} />
      {errorMessage && <div css={styles.error}>{errorMessage}</div>}
      <div css={styles.actions}>
        <CommonActionButton type='submit' disabled={!isValidOrgName(name) || createOrg.isPending}>
          {createOrg.isPending ? 'Creating...' : 'Create Org'}
        </CommonActionButton>
      </div>
    </form>
  );
};
